import React, {
  Fragment,
  HTMLAttributes,
  createElement,
  useCallback,
  useMemo,
} from 'react';
import { CloseIcon } from '../../assets/icons/CloseIcon';
import useScreenSize from '../../hooks/useScreenSize/useScreenSize';
import { ModalSizes } from '../../libraries/ModalSize';
import { ScreenSize } from '../../libraries/ScreenSizes';
import { getMergedClassNames, getUniqueKey } from '../../utils/HTMLutils';
import Button, { ButtonProps } from '../button/Button';
import IconButton from '../iconButton/IconButton';
import './Modal.scss';
const rootClassName = 'component-modal';

export interface ModalProps extends HTMLAttributes<HTMLDialogElement> {
  compenseColPaddingContent?: boolean;
  footer?: ButtonProps[] | JSX.Element;
  mobileOn?: ScreenSize;
  modalKey?: string;
  onClose?: (modalKey?: string | number) => void;
  preventEscExit?: boolean;
  preventMaskExit?: boolean;
  size?: ModalSizes;
  tag?: 'dialog' | 'div';
  title?: string;
}

export default function Modal({
  className,
  compenseColPaddingContent,
  footer,
  id,
  mobileOn,
  modalKey,
  onClose,
  preventEscExit,
  preventMaskExit,
  size,
  tag = 'dialog',
  title,
  ...props
}: ModalProps) {
  const { rankedSize } = useScreenSize();

  const modalProps = useMemo(
    () => ({
      ...props,
      id: id ?? modalKey ?? `modal${getUniqueKey()}`,
      className: getMergedClassNames([
        className || '',
        rootClassName,
        mobileOn !== undefined && rankedSize <= mobileOn ? 'mobile' : '',
        `${rootClassName}-${String(size).toLowerCase()}`,
      ]),
    }),
    [className, id, mobileOn, modalKey, props, rankedSize, size]
  );

  const footerElement = useMemo(() => {
    if (!footer) return null;
    if (Array.isArray(footer)) {
      return footer.map((props, index) => {
        return <Button key={index} {...props} />;
      });
    }
    return footer;
  }, [footer]);

  const handleClose = useCallback(() => {
    onClose?.(modalKey);
  }, [modalKey, onClose]);

  return createElement(
    tag,
    modalProps,
    <Fragment>
      <div className={`${rootClassName}-header`}>
        <div className={`${rootClassName}-title`}>{title}</div>
        <div className={`${rootClassName}-close-button`}>
          <IconButton onClick={handleClose} icon={<CloseIcon />} />
        </div>
      </div>
      <div
        className={getMergedClassNames([
          `${rootClassName}-content`,
          compenseColPaddingContent ? 'compense' : '',
        ])}
      >
        {props.children}
      </div>
      {footerElement ? (
        <div className={`${rootClassName}-footer`}>{footerElement}</div>
      ) : undefined}
    </Fragment>
  );
}
